import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingCard = ({ plan, billingCycle, price, isCurrentPlan, onSelect, onContactSales }) => {
  const isEnterprise = plan?.id === 'enterprise';

  return (
    <div
      className={`bg-card rounded-xl p-6 md:p-8 border-2 transition-all ${
        plan?.recommended
          ? 'border-accent shadow-warm-lg scale-105 relative'
          : 'border-border shadow-warm-sm hover:shadow-warm-md hover:border-accent/50'
      }`}
    >
      {/* Recommended Badge */}
      {plan?.recommended && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-accent text-accent-foreground px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 shadow-md">
            <Icon name="Star" size={14} />
            <span>Recommended</span>
          </div>
        </div>
      )}
      {/* Plan Header */}
      <div className="text-center mb-6">
        <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
          {plan?.name}
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          {plan?.description}
        </p>

        {/* Pricing */}
        <div className="mb-6">
          <div className="flex items-baseline justify-center gap-2">
            <span className="text-4xl md:text-5xl font-heading font-bold text-foreground">
              ${price}
            </span>
            <span className="text-muted-foreground">
              /{billingCycle === 'monthly' ? 'mo' : 'yr'}
            </span>
          </div>
          {billingCycle === 'annual' && (
            <p className="text-sm text-success mt-2">
              ${Math.round(plan?.annualPrice / 12)}/month billed annually
            </p>
          )}
        </div>

        {/* CTA Button */}
        {isEnterprise ? (
          <Button
            variant="outline"
            size="lg"
            fullWidth
            iconName="MessageCircle"
            onClick={onContactSales}
          >
            Contact Sales
          </Button>
        ) : isCurrentPlan ? (
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            iconName="Check"
            disabled
          >
            Current Plan
          </Button>
        ) : (
          <Button
            variant={plan?.recommended ? 'default' : 'outline'}
            size="lg"
            fullWidth
            iconName="ArrowRight"
            iconPosition="right"
            onClick={onSelect}
          >
            Choose {plan?.name}
          </Button>
        )}
      </div>
      {/* Features List */}
      <div className="space-y-3">
        <div className="border-t border-border pt-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Key Features
          </p>
        </div>
        {plan?.features?.slice(0, 6)?.map((feature, index) => (
          <div key={index} className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                feature?.included
                  ? 'bg-success/10' :'bg-muted'
              }`}
            >
              <Icon
                name={feature?.included ? 'Check' : 'X'}
                size={12}
                color={feature?.included ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
              />
            </div>
            <div className="flex-1">
              <p className={`text-sm ${
                feature?.included ? 'text-foreground' : 'text-muted-foreground line-through'
              }`}>
                {feature?.name}
              </p>
              {feature?.included && feature?.value && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {feature?.value}
                </p>
              )}
            </div>
          </div>
        ))}

        {/* View All Features Link */}
        {plan?.features?.length > 6 && (
          <button className="text-sm text-accent hover:text-accent/80 font-medium flex items-center gap-1 mt-4">
            <span>View all {plan?.features?.length} features</span>
            <Icon name="ChevronDown" size={14} />
          </button>
        )}
      </div>
      {/* Usage Limits */}
      <div className="mt-6 pt-6 border-t border-border">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Usage Limits
        </p>
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Job Postings</span>
            <span className="font-semibold text-foreground">
              {typeof plan?.limits?.jobPostings === 'number'
                ? `${plan?.limits?.jobPostings}/month`
                : plan?.limits?.jobPostings}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Active Jobs</span>
            <span className="font-semibold text-foreground">
              {typeof plan?.limits?.activeJobs === 'number'
                ? plan?.limits?.activeJobs
                : plan?.limits?.activeJobs}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Candidate Views</span>
            <span className="font-semibold text-foreground">
              {typeof plan?.limits?.candidateViews === 'number'
                ? `${plan?.limits?.candidateViews}/month`
                : plan?.limits?.candidateViews}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingCard;